import { BillingCycleTypeValue } from '@ts/billing-app.types';
import * as Sentry from '@sentry/react';
import { BillingSubscriptionCycle } from 'respona_api/generated/common_pb';
import { getAttribution } from '@helpers/attribution';

declare const IS_DEV: boolean;
declare const REALM: string;
declare const GTM_URL: string;

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const isProd = (): boolean => {
  try {
    return !IS_DEV;
  } catch {
    return false;
  }
};

const debugLog = (...args: unknown[]): void => {
  if (!isProd()) {
    // eslint-disable-next-line no-console
    console.log('[GTM]', ...args);
  }
};

const getOrCreateDataLayer = (): any[] | null => {
  if (!isBrowser()) return null;
  try {
    // @ts-ignore - dataLayer is injected by GTM and may not be in Window typings
    window.dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : [];
    // @ts-ignore
    return window.dataLayer as any[];
  } catch {
    return null;
  }
};

const safePushToDataLayer = (payload: Record<string, unknown>): void => {
  const dl = getOrCreateDataLayer();
  if (!dl) return;
  try {
    dl.push(payload);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while pushing GTM event', e);
    Sentry.captureException(e);
  }
};

const init = (gtmId: string): void => {
  if (!isBrowser()) return;
  if (!gtmId) return;

  try {
    const dl = getOrCreateDataLayer();
    if (!dl) return;

    const src = `${GTM_URL}/gtm.js?id=${gtmId}`;
    const alreadyInjected = Array.from(document.getElementsByTagName('script')).some((s) => s.src === src);

    if (!alreadyInjected) {
      dl.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
        realm: REALM,
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      document.head.appendChild(script);
      debugLog('Injected GTM script', src);
    } else {
      dl.push({
        realm: REALM,
      });
      debugLog('GTM script already present, skipping injection');
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize GTM', e);
    Sentry.captureException(e);
  }
};

export type AuthMethod = 'email' | 'google' | 'microsoft' | 'invitation';

/**
 * Flat attribution fields for the server-side GTM pipeline (TRACK-011). First touch is
 * the cohort dimension; last-touch source is included for debugging. Values are raw and
 * untrusted; the sGTM container decides what to forward.
 */
const composeAttributionFields = (): Record<string, unknown> => {
  try {
    const attribution = getAttribution();
    const first = attribution?.firstTouch;
    if (!attribution || (!first && !attribution.lastTouch)) return {};

    return {
      ...(first?.source ? { ft_source: first.source } : {}),
      ...(first?.medium ? { ft_medium: first.medium } : {}),
      ...(first?.campaign ? { ft_campaign: first.campaign } : {}),
      ...(first?.term ? { ft_term: first.term } : {}),
      ...(first?.content ? { ft_content: first.content } : {}),
      ...(first?.referrer ? { ft_referrer: first.referrer } : {}),
      ...(first?.landingPage ? { ft_landing_page: first.landingPage } : {}),
      ...(first?.capturedAt ? { ft_captured_at: first.capturedAt } : {}),
      // Click ids are flattened to one param each (ft_fbclid, ft_gclid, ...):
      // GA4 cannot ingest a nested object as an event param (QA 2026-08-28, F5).
      // Key names must stay in sync with the sGTM container's tag mappings.
      ...Object.entries(first?.clickIds ?? {}).reduce<Record<string, string>>(
        (acc, [name, value]) => {
          acc[`ft_${name}`] = value;
          return acc;
        },
        {}
      ),
      ...(attribution.lastTouch?.source ? { lt_source: attribution.lastTouch.source } : {}),
    };
  } catch {
    return {};
  }
};

type SignUpParams = {
  userId: number;
  email: string;
  method?: AuthMethod;
};

type SignInParams = {
  userId: number;
  email?: string;
  method?: AuthMethod;
};

const signUp = ({ userId, email, method = 'email' }: SignUpParams): void =>
  publishEvent({
    event: 'free_signup',
    method,
    user_id: userId,
    email,
    ...composeAttributionFields(),
  });

const signIn = ({ userId, email, method = 'email' }: SignInParams): void =>
  publishEvent({
    event: 'login',
    method,
    user_id: userId,
    ...(email ? { email } : {}),
  });

const makePayment = (
  email: string,
  dueTodayPrice: number,
  transactionId: string,
  item: string,
  subscriptionTotalPrice: number,
  billingCycle: BillingCycleTypeValue,
  quantity: number = 1
): void => {
  // GA4 dedupes purchases by transaction_id; an empty id silently drops every event after the first.
  if (!transactionId) {
    debugLog('Skipping purchase event - empty transaction_id', { item, dueTodayPrice });
    return;
  }

  function convertBillingCycle(value: BillingCycleTypeValue): string {
    switch (value) {
      case BillingSubscriptionCycle.NONE:
        return 'One-time';
      case BillingSubscriptionCycle.MONTHLY:
        return 'Monthly';
      case BillingSubscriptionCycle.YEARLY:
        return 'Annually';
      default:
        return 'Unknown';
    }
  }

  // Clear any prior ecommerce payload so GA4/GTM does not merge stale items/value into this event.
  safePushToDataLayer({ ecommerce: null });
  // NOTE (TRACK-011): every user-initiated charge calls this with a unique
  // transaction_id, so repeat purchases fire (GA4 dedupe only drops true duplicates).
  // Automatic renewals never reach the browser at all; those flow through
  // ANALYTICS_BILLING_ACTIONS server-side and are covered by the LTV report, not GTM.
  publishEvent({
    event: 'purchase',
    ...composeAttributionFields(),
    ecommerce: {
      currency: 'USD',
      value: dueTodayPrice,
      transaction_id: transactionId,
      items: [
        {
          item_name: item,
          price: subscriptionTotalPrice,
          item_category: convertBillingCycle(billingCycle),
          quantity,
        },
      ],
    },
  });
};

const publishEvent = (payload: Record<string, unknown>): void => {
  if (!isBrowser()) return;
  try {
    const realmPayload = {
      ...payload,
      realm: REALM,
    };
    debugLog('Publishing GTM event', realmPayload);
    safePushToDataLayer(realmPayload);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while publishing GTM event', e);
    Sentry.captureException(e);
  }
};

export default {
  init,
  signUp,
  signIn,
  makePayment,
};
